import React, { useEffect, useRef, useState } from "react";

export type LongTextVariant = "truncate" | "multilineFull" | "multilineInline";

interface LongTextProps {
  text: string | number;
  className?: string;
  maxLength?: number;
  /**
   * truncate: 従来どおり一行省略＋タップで展開
   * multilineFull: 折り返して全文表示（検索結果カードの物件名など）
   * multilineInline: 行内で折り返し（属性ラベルと同一行から続く）
   */
  variant?: LongTextVariant;
}

const LongTextMultilineFull: React.FC<{ textString: string; className: string }> = ({
  textString,
  className,
}) => (
  <span
    className={`block w-full min-w-0 max-w-full whitespace-normal break-words ${className}`.trim()}
  >
    {textString}
  </span>
);

/** 行内折り返し（ラベルと同じ行から続き、自然に改行） */
const LongTextMultilineInline: React.FC<{ textString: string; className: string }> = ({
  textString,
  className,
}) => (
  <span
    className={`min-w-0 whitespace-normal break-words hyphens-auto ${className}`.trim()}
  >
    {textString}
  </span>
);

const LongTextTruncate: React.FC<{
  textString: string;
  className: string;
  maxLength?: number;
}> = ({ textString, className, maxLength }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const expandedTextRef = useRef<HTMLDivElement>(null);

  const effectiveMaxLength = maxLength || 50;

  const shouldTruncate = textString.length > effectiveMaxLength;

  const displayText = shouldTruncate ? `${textString.substring(0, effectiveMaxLength)}...` : textString;

  const handleClick = () => {
    if (shouldTruncate) {
      setIsExpanded(!isExpanded);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isExpanded &&
        expandedTextRef.current &&
        textRef.current &&
        !expandedTextRef.current.contains(event.target as Node) &&
        !textRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded]);

  return (
    <div className="relative inline-block max-w-full">
      <span
        ref={textRef}
        className={`${className} ${shouldTruncate ? "cursor-pointer" : ""} truncate inline-block whitespace-nowrap overflow-hidden text-ellipsis max-w-full`}
        onClick={handleClick}
        title={textString}
      >
        {displayText}
      </span>

      {isExpanded && shouldTruncate && (
        <div
          ref={expandedTextRef}
          className="absolute top-full left-0 z-10 mt-1 max-w-[90%] rounded-md border border-gray-700 bg-gray-800 p-3 text-white shadow-lg"
        >
          <p className="break-words text-sm">{textString}</p>
        </div>
      )}
    </div>
  );
};

const LongText: React.FC<LongTextProps> = ({
  text,
  maxLength,
  className = "",
  variant = "truncate",
}) => {
  const textString = String(text);

  if (variant === "multilineFull") {
    return <LongTextMultilineFull className={className} textString={textString} />;
  }

  if (variant === "multilineInline") {
    return <LongTextMultilineInline className={className} textString={textString} />;
  }

  return <LongTextTruncate className={className} maxLength={maxLength} textString={textString} />;
};

export default LongText;
